import React, { useRef, useEffect } from 'react';
import { Frame, TitleBar } from '@react95/core';
import { useWindowManager } from '../contexts/windowContext';
import ClassicMenu from './ClassicMenu';
import { useWindowPlacement, TASKBAR_HEIGHT } from './windowPlacement';

// Metadata updates must not unregister the window or steal application focus.
function Win95Window({ id, title, icon, children, onClose, menu = [], style = {}, draggable = true, closeOnEscape = false }) {
    const { openWindow, closeWindow, updateWindow, focusWindow, getZIndex, activeWindowId } = useWindowManager();
    const ref = useRef(null);
    const initial = useRef({ title, icon });
    const { placement, handlers } = useWindowPlacement(ref, activeWindowId === id, draggable);
    useEffect(() => {
        const previousFocus = document.activeElement;
        openWindow(id, initial.current.title, initial.current.icon);
        return () => { closeWindow(id); if (previousFocus?.isConnected) previousFocus.focus(); };
    }, [id, openWindow, closeWindow]);
    useEffect(() => { updateWindow(id, title, icon); }, [id, title, icon, updateWindow]);
    useEffect(() => {
        if (activeWindowId === id && !ref.current?.contains(document.activeElement)) ref.current?.focus();
    }, [activeWindowId, id, title]);

    return <Frame ref={ref} position="fixed" padding="$2" backgroundColor="$material" boxShadow="$out"
        role="dialog" aria-label={title} tabIndex={-1}
        style={{ display: 'flex', flexDirection: 'column', top: '50px', color: 'black', ...style,
            // The active title bar indicates window focus; keep native focus rings on child controls.
            ...placement, zIndex: getZIndex(id), boxSizing: 'border-box', outline: 'none',
            maxWidth: '100vw', maxHeight: `calc(100dvh - ${TASKBAR_HEIGHT}px)` }}
        onPointerDown={() => focusWindow(id)} onFocus={() => { if (activeWindowId !== id) focusWindow(id); }}
        onKeyDown={e => { if (e.key === 'Escape' && closeOnEscape) { e.stopPropagation(); onClose?.(); } }}>
        <div {...handlers} style={{ touchAction: 'none', flexShrink: 0 }}>
            <TitleBar active={activeWindowId === id} icon={icon} title={title} mb="$2">
                <TitleBar.OptionsBox><TitleBar.Close onClick={onClose} /></TitleBar.OptionsBox>
            </TitleBar>
        </div>
        {menu.length > 0 && <ClassicMenu menus={menu} />}
        {children}
    </Frame>;
}
export default React.memo(Win95Window);
